import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    MenuItem,
    TablePagination,
    Chip,
    Button,
    CircularProgress,
    TableFooter
} from "@mui/material";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import SearchIcon from '@mui/icons-material/Search';

function Datewiseoutstanding() {
    // Database State
    const [customers, setCustomers] = useState([]);
    const [trips, setTrips] = useState([]);
    const [payments, setPayments] = useState([]);

    // Filters
    const [filterDays, setFilterDays] = useState("all");
    const [search, setSearch] = useState("");

    // Result State
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Pagination State
    const [page, setPage] = useState(0);
    const rowsPerPage = 10;

    // 1. Sync Data from Firestore
    useEffect(() => {
        const unsubCustomers = onSnapshot(
            collection(db, "customers"),
            (snap) => {
                const data = snap.docs.map((d) => ({
                    id: d.id,
                    ...d.data()
                }));
                setCustomers(data);
            },
            (error) => {
                console.error("CUSTOMER FETCH ERROR:", error);
            }
        );
        const unsubTrips = onSnapshot(collection(db, "trips"), (snap) => {
            setTrips(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        });
        const unsubPayments = onSnapshot(collection(db, "customer_history"), (snap) => {
            setPayments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        });
        
        return () => { unsubCustomers(); unsubTrips(); unsubPayments(); };
    }, []);

    // 2. Main Logic Function triggered by Button
    const handleGenerateReport = () => {
        setLoading(true);
        setPage(0);

        const results = customers.map((customer) => {
            const customerTrips = trips.filter((t) => t.customerId === customer.id);

            const totalSale = customerTrips.reduce((sum, trip) => {
                return sum + (trip.products?.reduce((s, p) => s + Number(p.amount || 0), 0) || 0);
            }, 0);

            const totalPaid = payments
                .filter((p) => p.customerId === customer.id)
                .reduce((sum, p) => sum + Number(p.amount || 0), 0);

            const remaining = totalSale - totalPaid;

            let lastTripDate = null;
            let daysDiff = -1;

            if (customerTrips.length > 0) {
                const dates = customerTrips
                    .map(t => {
                        if (!t.grossDate) return null;
                        return t.grossDate.toDate ? t.grossDate.toDate() : new Date(t.grossDate);
                    })
                    .filter(d => d instanceof Date && !isNaN(d));

                if (dates.length > 0) {
                    lastTripDate = new Date(Math.max(...dates));
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const compareDate = new Date(lastTripDate);
                    compareDate.setHours(0, 0, 0, 0);
                    daysDiff = Math.floor((today - compareDate) / (1000 * 60 * 60 * 24));
                }
            }

            return { ...customer, totalSale, totalPaid, remaining, lastTripDate, daysDiff };
        }).filter((item) => {
            const hasOutstanding = item.remaining > 0;
            if (!hasOutstanding) return false;

            // Apply Aging Filter
            if (filterDays === "10") return item.daysDiff >= 0 && item.daysDiff <= 10;
            if (filterDays === "30") return item.daysDiff >= 0 && item.daysDiff <= 30;
            if (filterDays === "180") return item.daysDiff >= 0 && item.daysDiff <= 180;

            return true;
        });

        // Sort by highest outstanding balance (Optional but recommended)
        results.sort((a, b) => b.remaining - a.remaining);

        setReportData(results);
        setLoading(false);
    };

    // 3. Dynamic Search Filter & Totals
    const displayedData = reportData.filter((item) => {
        const customerName = (item.customerName || item.name || "").toLowerCase();
        return customerName.includes(search.toLowerCase());
    });

    const totalOutstandingAmount = displayedData.reduce((sum, item) => sum + item.remaining, 0);

    // Format Currency
    const formatMoney = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

    return (
        <Box p={3} bgcolor="#f9f9f9" minHeight="100vh">
            <Typography variant="h5" fontWeight="bold" mb={3}>
                📅 Date-wise Outstanding Report
            </Typography>

            <Box display="flex" gap={2} mb={3} alignItems="center" flexWrap="wrap">
                {/* Simple Text Search for Customer Name */}
                <TextField
                    size="small"
                    label="Search Customer Name"
                    variant="outlined"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(0); // Reset to first page on search
                    }}
                    sx={{ width: 280, bgcolor: "white" }}
                />

                <TextField
                    select
                    size="small"
                    label="Ageing Period"
                    value={filterDays}
                    onChange={(e) => setFilterDays(e.target.value)}
                    sx={{ width: 200, bgcolor: "white" }}
                >
                    <MenuItem value="all">All Pending</MenuItem>
                    <MenuItem value="10">Last 10 Days</MenuItem>
                    <MenuItem value="30">Last 30 Days</MenuItem>
                    <MenuItem value="180">Last 6 Months</MenuItem>
                </TextField>

                <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    onClick={handleGenerateReport}
                    disabled={loading}
                    sx={{ height: 40 }}
                >
                    Get Data
                </Button>
            </Box>

            <Paper elevation={3}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: "#eee" }}>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Customer</TableCell>
                            <TableCell>Last Trip</TableCell>
                            <TableCell>Ageing</TableCell>
                            <TableCell align="right">Outstanding</TableCell>
                        </TableRow>
                    </TableHead>
                    
                    <TableBody>
                        {displayedData.length > 0 ? (
                            displayedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => (
                                <TableRow key={item.id} hover>
                                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>
                                        {item.customerName || item.name || "Unnamed Customer"}
                                    </TableCell>
                                    <TableCell>{item.lastTripDate ? item.lastTripDate.toLocaleDateString('en-IN') : "N/A"}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={item.daysDiff === -1 ? "No Trips" : `${item.daysDiff} Days`}
                                            color={item.daysDiff > 30 ? "error" : "primary"}
                                        />
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: "red", fontWeight: "bold" }}>
                                        {formatMoney(item.remaining)}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                    <Typography color="textSecondary">
                                        {reportData.length === 0 
                                            ? "Click 'Get Data' to display the outstanding report." 
                                            : "No customers match your search criteria."}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>

                    {/* Overall Total Display */}
                    {displayedData.length > 0 && (
                        <TableFooter>
                            <TableRow sx={{ bgcolor: "#fafafa" }}>
                                <TableCell colSpan={4} align="right" sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                                    Total Outstanding:
                                </TableCell>
                                <TableCell align="right" sx={{ color: "red", fontWeight: "bold", fontSize: "1.1rem" }}>
                                    {formatMoney(totalOutstandingAmount)}
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    )}
                </Table>
                
                <TablePagination
                    rowsPerPageOptions={[10]}
                    component="div"
                    count={displayedData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                />
            </Paper>
        </Box>
    );
}

export default Datewiseoutstanding;